// Performance Monitoring Utility for Analytics API
import { backendHealth } from './backendHealth';

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.thresholds = {
      slow: 2000, // 2 seconds
      verySlow: 5000, // 5 seconds
      timeout: 10000, // 10 seconds
    };
    this.alerts = [];
    this._recentErrorEndpoints = new Map(); // endpoint -> { count, lastLogged }
    this._ERROR_LOG_INTERVAL = 30000; // Only log same error endpoint once per 30s
    this._suppressedAlertsSinceLastSummary = 0;
    this._lastSuppressedSummaryAt = 0;
    this._SUPPRESSED_SUMMARY_INTERVAL_MS = 60000;
  }

  // Start monitoring a request
  startRequest(requestId, endpoint, method = 'GET') {
    const startTime = performance.now();
    this.metrics.set(requestId, {
      endpoint,
      method,
      startTime,
      status: 'pending',
    });
    return requestId;
  }

  // End monitoring a request
  endRequest(requestId, success = true, error = null) {
    const endTime = performance.now();
    const metric = this.metrics.get(requestId);

    if (!metric) {
      console.warn(`PerformanceMonitor: Request ${requestId} not found`);
      return null;
    }

    const duration = endTime - metric.startTime;
    const updatedMetric = {
      ...metric,
      endTime,
      duration: Math.round(duration),
      success,
      error: error?.message || null,
      status: success ? 'completed' : 'failed',
    };

    this.metrics.set(requestId, updatedMetric);

    // Check for performance issues
    this.checkPerformance(updatedMetric);

    return updatedMetric;
  }

  // Check if request performance meets thresholds
  checkPerformance(metric) {
    const { endpoint, duration, success } = metric;
    const isCameraConnectionCheck =
      /^\/api\/v1\/admin\/cameras\/[^/]+\/test-connection$/.test(
        endpoint || ''
      );

    // When the shared health monitor knows the backend is down, suppress
    // per-request alerts entirely. Instead emit a single summary line every
    // 60s so operators still see how much noise was being silenced.
    if (backendHealth.getState().isDown) {
      this._suppressedAlertsSinceLastSummary += 1;
      const now = Date.now();
      if (
        now - this._lastSuppressedSummaryAt >=
        this._SUPPRESSED_SUMMARY_INTERVAL_MS
      ) {
        const count = this._suppressedAlertsSinceLastSummary;
        this._suppressedAlertsSinceLastSummary = 0;
        this._lastSuppressedSummaryAt = now;
        // eslint-disable-next-line no-console
        console.log(
          `[performanceMonitor] suppressed ${count} alerts while backend is down`
        );
      }
      return;
    }

    if (!success) {
      if (endpoint === '/api/v1/users/me') return;

      // Rate-limit repeated error logs for the same endpoint
      const now = Date.now();
      const recent = this._recentErrorEndpoints.get(endpoint);
      if (recent && now - recent.lastLogged < this._ERROR_LOG_INTERVAL) {
        recent.count++;
        return; // Suppress repeated log, still tracked internally
      }
      const suppressed = recent ? recent.count : 0;
      const suffix =
        suppressed > 0 ? ` (${suppressed} similar errors suppressed)` : '';
      this._recentErrorEndpoints.set(endpoint, { count: 0, lastLogged: now });
      this.addAlert('error', `Request failed: ${endpoint}${suffix}`, metric);
      return;
    }

    // Camera test-connection is an on-demand diagnostic call that can be slow by design.
    // Skip slow alerts for this endpoint to avoid noisy console spam.
    if (isCameraConnectionCheck) {
      return;
    }

    if (duration > this.thresholds.timeout) {
      this.addAlert(
        'critical',
        `Request timeout: ${endpoint} (${duration}ms)`,
        metric
      );
    } else if (duration > this.thresholds.verySlow) {
      this.addAlert(
        'warning',
        `Very slow request: ${endpoint} (${duration}ms)`,
        metric
      );
    } else if (duration > this.thresholds.slow) {
      this.addAlert(
        'info',
        `Slow request: ${endpoint} (${duration}ms)`,
        metric
      );
    }
  }

  // Add performance alert
  addAlert(level, message, metric) {
    const alert = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      level,
      message,
      metric,
    };

    this.alerts.push(alert);

    // Log alert
    const emoji =
      {
        info: 'ℹ️',
        warning: '⚠️',
        critical: '🚨',
        error: '❌',
      }[level] || '📝';

    console.log(`${emoji} Performance Alert: ${message}`);

    // Trigger custom event for UI components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('performanceAlert', { detail: alert })
      );
    }
  }

  // Get performance statistics
  getStats() {
    const metrics = Array.from(this.metrics.values());
    const completed = metrics.filter(m => m.status === 'completed');
    const failed = metrics.filter(m => m.status === 'failed');

    if (completed.length === 0) {
      return {
        totalRequests: metrics.length,
        completedRequests: 0,
        failedRequests: failed.length,
        averageResponseTime: 0,
        minResponseTime: 0,
        maxResponseTime: 0,
        successRate: 0,
        slowRequests: 0,
        verySlowRequests: 0,
        timeoutRequests: 0,
      };
    }

    const responseTimes = completed.map(m => m.duration);
    const averageResponseTime = Math.round(
      responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
    );

    const slowRequests = completed.filter(
      m => m.duration > this.thresholds.slow
    ).length;
    const verySlowRequests = completed.filter(
      m => m.duration > this.thresholds.verySlow
    ).length;
    const timeoutRequests = completed.filter(
      m => m.duration > this.thresholds.timeout
    ).length;

    return {
      totalRequests: metrics.length,
      completedRequests: completed.length,
      failedRequests: failed.length,
      averageResponseTime,
      minResponseTime: Math.min(...responseTimes),
      maxResponseTime: Math.max(...responseTimes),
      successRate: Math.round((completed.length / metrics.length) * 100),
      slowRequests,
      verySlowRequests,
      timeoutRequests,
    };
  }

  // Get endpoint-specific statistics
  getEndpointStats(endpoint) {
    const metrics = Array.from(this.metrics.values()).filter(
      m => m.endpoint === endpoint
    );

    if (metrics.length === 0) {
      return null;
    }

    const completed = metrics.filter(m => m.status === 'completed');
    const failed = metrics.filter(m => m.status === 'failed');

    if (completed.length === 0) {
      return {
        endpoint,
        totalRequests: metrics.length,
        completedRequests: 0,
        failedRequests: failed.length,
        successRate: 0,
        averageResponseTime: 0,
      };
    }

    const responseTimes = completed.map(m => m.duration);
    const averageResponseTime = Math.round(
      responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
    );

    return {
      endpoint,
      totalRequests: metrics.length,
      completedRequests: completed.length,
      failedRequests: failed.length,
      successRate: Math.round((completed.length / metrics.length) * 100),
      averageResponseTime,
      minResponseTime: Math.min(...responseTimes),
      maxResponseTime: Math.max(...responseTimes),
    };
  }

  // Get recent alerts
  getRecentAlerts(limit = 10) {
    return this.alerts
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  }

  // Clear old metrics (keep last 100 requests)
  clearOldMetrics() {
    const metrics = Array.from(this.metrics.entries());
    if (metrics.length > 100) {
      const toKeep = metrics.slice(-100);
      this.metrics.clear();
      toKeep.forEach(([key, value]) => this.metrics.set(key, value));
    }
  }

  // Clear old alerts (keep last 50 alerts)
  clearOldAlerts() {
    if (this.alerts.length > 50) {
      this.alerts = this.alerts.slice(-50);
    }
  }

  // Generate performance report
  generateReport() {
    const stats = this.getStats();
    const recentAlerts = this.getRecentAlerts(5);

    console.log('📊 PERFORMANCE MONITOR REPORT');
    console.log('---');
    console.log(`Total Requests: ${stats.totalRequests}`);
    console.log(`Completed: ${stats.completedRequests}`);
    console.log(`Failed: ${stats.failedRequests}`);
    console.log(`Success Rate: ${stats.successRate}%`);
    console.log(`Average Response Time: ${stats.averageResponseTime}ms`);
    console.log(`Min Response Time: ${stats.minResponseTime}ms`);
    console.log(`Max Response Time: ${stats.maxResponseTime}ms`);
    console.log(`Slow Requests (>2s): ${stats.slowRequests}`);
    console.log(`Very Slow Requests (>5s): ${stats.verySlowRequests}`);
    console.log(`Timeout Requests (>10s): ${stats.timeoutRequests}`);

    if (recentAlerts.length > 0) {
      console.log('---');
      console.log('🚨 RECENT ALERTS');
      recentAlerts.forEach(alert => {
        const emoji =
          {
            info: 'ℹ️',
            warning: '⚠️',
            critical: '🚨',
            error: '❌',
          }[alert.level] || '📝';
        console.log(`${emoji} ${alert.message}`);
      });
    }

    return {
      stats,
      recentAlerts,
      timestamp: new Date().toISOString(),
    };
  }

  // Reset all metrics and alerts
  reset() {
    this.metrics.clear();
    this.alerts = [];
    console.log('🔄 Performance monitor reset');
  }
}

// Create global instance
const performanceMonitor = new PerformanceMonitor();

// Auto-cleanup every 5 minutes
if (typeof window !== 'undefined') {
  const cleanupInterval = setInterval(
    () => {
      performanceMonitor.clearOldMetrics();
      performanceMonitor.clearOldAlerts();
    },
    5 * 60 * 1000
  );

  // Attempt to provide a way to clear the interval if needed,
  // though this is a global singleton utility.
  performanceMonitor._cleanupInterval = cleanupInterval;
}

export default performanceMonitor;
